<template>
  <o-modal
    ref="dialog"
    :title="$t('general.attachment')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel'},
      { text: $t('general.attach'), type: 'confirm', callback: handleConfirm, disabled: !valid },
    ]"
    :loading="loading"
    modal-class="relative m-2 bg-gray-100 dark:bg-gray-900 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-2xl sm:w-full h-full max-h-lg"
    content-class="mx-auto max-w-2xl h-full min-h-0 flex flex-col"
  >
    <template #body>
      <o-tabs class="min-h-0" :show-arrows="false" nav-class="border-b border-gray-200 dark:border-gray-800" @changed="handleChangedTab">
        <o-tab id="upload" :name="$t('general.uploadAttachment')" icon="upload">
          <o-form v-if="!loading" ref="form" class="px-3 block">
            <o-field-group :name="$t('general.attachment')" class="block pb-4 mb-4 border-b">
              <o-upload v-model="_upload" v-model:caption="upload.caption" />
            </o-field-group>

            <o-field-group :label="$t('asset.addToLibrary')" :info="$t('asset.addToLibraryInfo')" class="block pb-3">
              <toggle-field v-model="upload.save" />
            </o-field-group>

            <o-field-group v-if="upload.save" :label="$t('asset.fileName')" :info="$t('components.campaign.actions.addAttachment.fileNameInfo')" class="block pb-3">
              <text-field v-model="upload.caption" />
            </o-field-group>
          </o-form>
        </o-tab>
        <o-tab id="assets" :name="$t('asset.library')" icon="library">
          <div class="h-full min-h-0 flex flex-col overflow-hidden">
            <div class="flex items-center justify-between px-3 mb-4 pb-2 -mt-2 border-b flex-shrink-0">
              <text-field
                v-model="assets.filters.search"
                :placeholder="$t('asset.search')"
                leading="search"
                clear
                class="w-72"
                @input="handleFilterChange"
              />

              <select-field
                v-model="assets.filters.dataType"
                :options="[
                  { text: $t('general.all'), value: null },
                  { text: $t('asset.images'), value: 0 },
                  { text: $t('asset.files'), value: 1 }
                ]"
                class="w-36"
                @change="handleFilterChange"
              />
            </div>
            <upload-pagination :pager="assets.pager" class="flex-shrink min-h-0 h-full flex-grow border-b">
              <template #default="{items}">
                <div class="flex items-start flex-wrap gap-3 mb-8 p-1">
                  <upload-grid
                    v-for="item in items"
                    :key="item.idPhoto"
                    :upload="item"
                    :class="{ 'ring-offset-2 ring' : assets.selected === item }"
                    @click="handleSelectAsset(item)"
                  />
                </div>
              </template>

              <template #empty>
                <div class="p-6">
                  <p class="text-center text-gray-800 font-semibold">
                    {{ $t('asset.noUploads') }}
                  </p>
                  <p class="text-center text-sm text-gray-600">
                    {{ assets.filters.search || assets.filters.dataType !== null ? $t('asset.adjustFilters') : $t('asset.uploadInfo') }}
                  </p>
                </div>
              </template>
            </upload-pagination>
          </div>
        </o-tab>
      </o-tabs>
    </template>
  </o-modal>
</template>

<script setup lang="ts">
import debounce from 'lodash/debounce'
import UploadPagination from '@/components/upload/elements/Pagination.vue'
import UploadGrid from '@/components/upload/elements/Grid.vue'
import OTabs from '@/components/elements/tabs/Tabs.vue'
import OTab from '@/components/elements/tabs/Tab.vue'
import OForm from '@/components/elements/Form.vue'
import OUpload from '@/components/elements/Upload.vue'
import {
  ToggleField,
  TextField,
  SelectField,
} from '@/components/fields'
import { EndlessListPager } from '~/_api/lib/pager'

const { $api, $notification, $i18n } = useNuxtApp()

interface Props {
  attachments?: any
  portal?: string
}

withDefaults(defineProps<Props>(), {
  attachments: null,
  portal: undefined,
})

const emit = defineEmits<{
  (e: 'success', response: any): void
  (e: 'update:attachments', attachments: any): void
}>()

const dialog = ref<any>()

const campaign = ref<any>(null)
const loading = ref(false)
const tab = ref('upload')

const upload = ref({
  file: null as File | null,
  save: true,
  caption: '',
})

const assets = ref({
  pager: null as any,
  selected: null as any,
  filters: {
    search: '',
    dataType: null as number | null,
  },
})

const _upload = computed({
  get () {
    if (upload.value.file instanceof File) {
      return upload.value.file
    }

    return null
  },
  set (value: File | null) {
    upload.value.file = value
  },
})

const isTabUpload = computed(() => {
  return tab.value === 'upload'
})

const isTabAssets = computed(() => {
  return tab.value === 'assets'
})

const valid = computed(() => {
  if (isTabUpload.value) {
    return !!upload.value.file
  }

  if (isTabAssets.value) {
    return !!assets.value.selected
  }

  return false
})

function open (campaignData: any) {
  upload.value.file = null
  upload.value.caption = ''

  assets.value.filters = {
    search: '',
    dataType: null,
  }
  assets.value.pager = new EndlessListPager((page: number) => {
    return $api.marketing.uploads({
      page,
      search: assets.value.filters.search,
      dataType: assets.value.filters.dataType,
    })
  }, 20)

  assets.value.selected = null

  campaign.value = campaignData

  dialog.value.open()
}

const handleFilterChange = debounce(() => {
  assets.value.pager.reload()
}, 500)

async function handleConfirm () {
  loading.value = true

  try {
    let params: any = {}

    // If adding an existing attachment
    if (isTabAssets.value) {
      params = {
        idPhoto: assets.value.selected.idPhoto,
      }
    }

    // If uploading a new attachment
    if (isTabUpload.value) {
      // If saving to asset library
      if (upload.value.save) {
        const file = upload.value.file
        const caption = upload.value.caption
        const uploadResponse = await $api.upload.upload({ file, params: { caption } })

        if (uploadResponse?.data) {
          params = {
            idPhoto: (uploadResponse.data as any).idPhoto,
          }
        }

        // If not saving to asset library
      } else {
        params = {
          attachment: upload.value.file,
        }
      }
    }

    const response = await $api.campaign.addAttachment(campaign.value, params)

    if (response?.data) {
      $notification.toast({
        title: $i18n.t('components.campaign.actions.addAttachment.success'),
        text: $i18n.t('components.campaign.actions.addAttachment.successInfo'),
        variant: 'success',
      })

      emit('success', response.data)
      emit('update:attachments', (response.data as any).attachments)

      loading.value = false

      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

function handleSelectAsset (item: any) {
  assets.value.selected = item
}

function handleChangedTab (tabData: any) {
  tab.value = tabData?.tab?.hash?.replace('#', '')
}

defineExpose({ open })
</script>
